import React, { useState, useEffect } from 'react';
import T from '../../components/common/T'; 
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosConfig';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserPlusIcon, BriefcaseIcon, BanknotesIcon, BuildingLibraryIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { useCustomFields } from '../../hooks/useCustomFields';
import CustomFieldRenderer from '../../components/common/CustomFieldRenderer';

const AddWorkforce = () => {
  const navigate = useNavigate();
  const { checkAccess } = useAuth();

  // --- 1. STATE MANAGEMENT ---
  const [loading, setLoading] = useState(false);
  const { customData, setCustomData, saveCustomFields } = useCustomFields('workforce', null);
  const [roles, setRoles] = useState([]); 
  
  // Form Data
  const [formData, setFormData] = useState({
    employee_id: '',
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: 'Male',
    role: '', 
    department: 'Management',
    employment_type: 'Full-Time',
    date_of_joining: new Date().toISOString().split('T')[0], 
    address: '',
    city: '',
    country: 'India',
    
    // 🟢 NEW FINANCIAL CONTRACT FIELDS
    salary_type: 'Fixed',
    base_salary: '',
    overtime_rate: '',
    standard_daily_hours: 8,
    
    bank_name: '',
    bank_account_no: '',
    tax_id: '',
  });

  // --- 2. FETCH DATA ON LOAD ---
  useEffect(() => {
    if (checkAccess && !checkAccess('workforce.create')) {
        toast.error("Access Denied");
        navigate('/admin/workforce');
        return;
    }

    const fetchRoles = async () => {
        try {
            const res = await api.get('/roles');
            setRoles(res.data);
        } catch (err) {
            console.error("Failed to load roles", err);
            toast.error("Could not load roles list");
        }
    };

    fetchRoles();
  }, [checkAccess, navigate]);

  // --- 3. HANDLERS ---
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.employee_id) {
        return toast.warn("Please fill in all required fields (*)");
    }
    if (!formData.role) {
        return toast.warn("Please select a System Role");
    }
    if (!formData.base_salary) {
        return toast.warn("Please enter the Base Salary / Wage");
    }

    setLoading(true);
    try {
      const response = await api.post('/workforce', formData);

      await saveCustomFields(response.data.id);

      toast.success(response.data.message || "Employee added successfully!");
      
      if (response.data.generatedPassword) {
         toast.info(`System Password Generated: ${response.data.generatedPassword}`, { autoClose: 10000 });
      }

      setTimeout(() => navigate('/admin/workforce'), 2000);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to add employee");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen font-sans">
      
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-8">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <UserPlusIcon className="w-7 h-7 text-blue-600" />
          </div>
          <T>Onboard New Employee</T>
        </h2>
        <p className="text-gray-500 mt-2 text-sm">
            <T>Create a complete HR profile, configure payroll rules, and automatically generate system login credentials.</T>
        </p>
      </div>

      <div className="max-w-5xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* CARD 1: Personal Details */}
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-bold text-gray-800 mb-6 border-b pb-3 flex items-center gap-3">
                <span className="bg-blue-100 text-blue-700 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <T>Personal Details</T>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Employee ID <span className="text-red-500">*</span></label>
                <input type="text" name="employee_id" className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" placeholder="e.g. EMP-001" value={formData.employee_id} onChange={handleChange}/>
              </div>
              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Full Name <span className="text-red-500">*</span></label>
                <input type="text" name="name" className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" placeholder="John Doe" value={formData.name} onChange={handleChange}/>
              </div>
              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Email Address <span className="text-red-500">*</span></label>
                <input type="email" name="email" className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" placeholder="john@company.com" value={formData.email} onChange={handleChange}/>
              </div>
              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Phone</label>
                <input type="text" name="phone" className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" placeholder="+91 9876543210" value={formData.phone} onChange={handleChange}/>
              </div>
              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Date of Birth</label>
                <input type="date" name="date_of_birth" className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition text-gray-700" value={formData.date_of_birth} onChange={handleChange}/>
              </div>
              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Gender</label>
                <select name="gender" className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" value={formData.gender} onChange={handleChange}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* CARD 2: Job & Access */}
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-amber-500 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-5">
              <BriefcaseIcon className="w-32 h-32" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-6 border-b pb-3 flex items-center gap-3 relative z-10">
                <span className="bg-amber-100 text-amber-700 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <T>System Access & Role</T>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
              <div className="col-span-1 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <label className="block text-amber-900 text-xs font-bold uppercase tracking-wide mb-2">
                    Assign System Role <span className="text-red-500">*</span>
                </label>
                <select name="role" className="w-full p-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white font-medium outline-none" value={formData.role} onChange={handleChange}>
                  <option value="">-- Select Role --</option>
                  {roles.map(role => (
                      <option key={role.id} value={role.name}>{role.name}</option>
                  ))}
                </select>
                <p className="text-xs text-amber-700 mt-2 font-medium">This automatically generates their login credentials.</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Department</label>
                  <select name="department" className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" value={formData.department} onChange={handleChange}>
                    <option value="Management">Management</option>
                    <option value="IT">IT & Development</option>
                    <option value="HR">Human Resources</option>
                    <option value="Sales">Sales & Marketing</option>
                    <option value="Construction">Construction / On-Site</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Employment</label>
                    <select name="employment_type" className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" value={formData.employment_type} onChange={handleChange}>
                        <option value="Full-Time">Full-Time</option>
                        <option value="Part-Time">Part-Time</option>
                        <option value="Contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Join Date</label>
                    <input type="date" name="date_of_joining" className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition text-gray-700" value={formData.date_of_joining} onChange={handleChange}/>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 🟢 CARD 3: Payroll & Financial Contract */}
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-green-500">
            <h3 className="text-lg font-bold text-gray-800 mb-6 border-b pb-3 flex items-center gap-3">
              <span className="bg-green-100 text-green-700 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <T>Payroll & Financial Contract</T>
              <BanknotesIcon className="w-5 h-5 text-gray-400 ml-auto" />
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Salary Type <span className="text-red-500">*</span></label>
                <select name="salary_type" value={formData.salary_type} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none transition">
                  <option value="Fixed">Fixed Monthly Salary</option>
                  <option value="Hourly">Hourly Wage</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Base Salary / Wage (₹) <span className="text-red-500">*</span></label>
                <input type="number" name="base_salary" value={formData.base_salary} onChange={handleChange} placeholder={formData.salary_type === 'Fixed' ? "e.g. 45000" : "e.g. 500"} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none transition" required />
              </div>

              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Overtime Rate (₹/hr)</label>
                <input type="number" name="overtime_rate" value={formData.overtime_rate} onChange={handleChange} placeholder="e.g. 150" className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none transition" />
              </div>
              
              <div className="md:col-span-3">
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Standard Daily Hours</label>
                <input type="number" step="0.5" name="standard_daily_hours" value={formData.standard_daily_hours} onChange={handleChange} className="w-full md:w-1/3 p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none transition"/>
                <p className="text-xs text-gray-500 mt-2">Required for calculating absences and precise overtime payouts during final payroll.</p>
              </div>
            </div>
          </div>

          {/* CARD 4: Bank Details */}
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-purple-500">
            <h3 className="text-lg font-bold text-gray-800 mb-6 border-b pb-3 flex items-center gap-3">
              <span className="bg-purple-100 text-purple-700 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold">4</span>
              <T>Bank & Tax Details</T>
              <BuildingLibraryIcon className="w-5 h-5 text-gray-400 ml-auto" />
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Bank Name</label>
                <input type="text" name="bank_name" value={formData.bank_name} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none transition" />
              </div>
              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Account Number</label>
                <input type="text" name="bank_account_no" value={formData.bank_account_no} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none transition" />
              </div>
              <div>
                <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Tax ID / PAN</label>
                <input type="text" name="tax_id" value={formData.tax_id} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none transition" />
              </div>
            </div>
          </div>

          {/* CARD 5: Address */}
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-200">
             <h3 className="text-lg font-bold text-gray-800 mb-6 border-b pb-3 flex items-center gap-3">
                <span className="bg-gray-100 text-gray-600 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold">5</span>
                <T>Location Information</T>
                <MapPinIcon className="w-5 h-5 text-gray-400 ml-auto" />
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div>
                    <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">City</label>
                    <input type="text" name="city" onChange={handleChange} value={formData.city} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" />
                 </div>
                 <div>
                    <label className="block text-gray-700 text-xs font-bold uppercase tracking-wide mb-2">Full Address</label>
                    <input type="text" name="address" onChange={handleChange} value={formData.address} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" />
                 </div>
            </div>
          </div>

          {/* Custom Fields */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
             <CustomFieldRenderer module="workforce" customData={customData} onChange={setCustomData} />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-4 pb-12 sticky bottom-0 bg-gray-50/90 backdrop-blur-sm p-4 border-t border-gray-200">
            <button 
              type="button"
              onClick={() => navigate('/admin/workforce')}
              disabled={loading}
              className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
            >
              Cancel
            </button>
            
            <button 
              type="submit"
              disabled={loading}
              className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-md transition disabled:bg-blue-400 flex items-center gap-2 min-w-[200px] justify-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <UserPlusIcon className="w-5 h-5"/> 
                  Create Employee
                </>
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default AddWorkforce;